import { useEffect, useState, useCallback } from 'react';
import {
  doc, onSnapshot, addDoc, setDoc, collection, serverTimestamp, query, orderBy, deleteDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebase.js';
import { ESHOT_OWNER } from '../useAdmin.js';
import { currentCallerEmail } from '../lib/callerEmail.js';

// Allowed terminal/in-flight statuses — see spec §3.
export const SEND_STATUSES = [
  'draft', 'scheduled', 'pending_approval', 'pending_review',
  'sending', 'paused', 'sent', 'partial', 'failed', 'cancelled',
];

export function useSends() {
  const [sends, setSends] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'eshot_sends'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setSends(rows);
    }, (err) => {
      // Fallback for missing index
      console.warn('[useSends] ordered query failed, falling back:', err.message);
      const sub = onSnapshot(collection(db, 'eshot_sends'), (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        rows.sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0));
        setSends(rows);
      });
      return sub;
    });
    return unsub;
  }, []);

  return { sends: sends || [], loading: sends === null };
}

export function useSend(sendId) {
  const [send, setSend] = useState(null);
  const [loading, setLoading] = useState(!!sendId);

  useEffect(() => {
    if (!sendId) { setSend(null); setLoading(false); return; }
    const unsub = onSnapshot(doc(db, 'eshot_sends', sendId), (snap) => {
      setSend(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsub;
  }, [sendId]);

  return { send, loading };
}

// The authored fields Copy to a new draft carries over, each only when set.
const COPY_FIELDS = [
  'templateId', 'subject', 'content', 'audienceId', 'adHocFilters', 'adHocContacts',
  'aiBrief', 'aiOverrides', 'aiGenerated', 'attachments', 'sharePage', 'eventInvites', 'onBehalfOf',
];

/**
 * The fields a copy carries, and what it leaves off. My files uploads, on the
 * send and on its share page, are checked against the send's creator, and the
 * copier becomes the copy's creator, so someone else's uploads, and their
 * share page, are left off: kept, they would make the copy refused at send
 * time. Same person (any case): all kept. Sending as a colleague
 * (onBehalfOf) is the owner's alone (firestore.rules eshotSendAsOk), so it is
 * left off anyone else's copy rather than having the copy refused.
 */
export function draftCopyFields(send, copierEmail) {
  const fields = { name: `${send?.name || 'Untitled broadcast'} (copy)` };
  for (const key of COPY_FIELDS) {
    if (send && send[key] !== undefined && send[key] !== null) fields[key] = send[key];
  }
  const sameOwner = String(send?.createdBy || '').trim().toLowerCase() === String(copierEmail || '').trim().toLowerCase();
  const dropped = [];
  if (Array.isArray(fields.attachments) && !sameOwner) {
    fields.attachments = fields.attachments.filter((att) => {
      const theirs = att && att.source === 'userFile';
      if (theirs) dropped.push(att.name || att.id || 'a My files upload');
      return !theirs;
    });
  }
  if (fields.sharePage && !sameOwner) {
    dropped.push('the Media Hub share page (make a new one in the copy)');
    delete fields.sharePage;
  }
  if (fields.onBehalfOf && String(copierEmail || '').trim().toLowerCase() !== ESHOT_OWNER) {
    dropped.push('sending as a colleague (only Arran North can set that)');
    delete fields.onBehalfOf;
  }
  return { fields, dropped };
}

// createdBy as the create rule reads the caller (lib/callerEmail.js), never
// the account email, which differs for some staff.
async function creatorEmail() {
  const createdBy = await currentCallerEmail(auth);
  if (!createdBy) throw new Error('Your sign-in has no confirmed Trovex email. Sign out and sign in again.');
  return createdBy;
}

// A plain message for a refused draft write, instead of Firestore's raw
// "Missing or insufficient permissions".
export function draftWriteMessage(err) {
  if (err?.code === 'permission-denied') {
    return 'Broadcast would not let you save this. Check you are on the Broadcast list in the Systems users console, or ask Arran North.';
  }
  return err?.message || String(err);
}

export function useDraftActions() {
  const [error, setError] = useState(null);

  const createDraft = useCallback(async ({ templateId, name }) => {
    const createdBy = await creatorEmail();
    const ref = await addDoc(collection(db, 'eshot_sends'), {
      name: name || 'Untitled broadcast',
      templateId,
      subject: '',
      content: { slotsHidden: [] },
      audienceId: null,
      adHocFilters: null,
      recipientCount: 0,
      status: 'draft',
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      stats: { delivered: 0, opened: 0, clicked: 0, bounced: 0, complained: 0, unsubscribed: 0 },
    });
    return ref.id;
  }, []);

  // A new, editable draft with a stopped send's content (a failed or
  // cancelled send cannot be edited or rescheduled). Only what the author
  // wrote is copied: never its status, approval, dispatch, stats or recipients.
  // `fields` is draftCopyFields(send, copier).fields, worked out once by the
  // caller so what it warned about is exactly what is written.
  const copyToDraft = useCallback(async (fields) => {
    const createdBy = await creatorEmail();
    const ref = await addDoc(collection(db, 'eshot_sends'), {
      ...fields,
      status: 'draft',
      recipientCount: 0,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastEditedAt: serverTimestamp(),
      stats: { delivered: 0, opened: 0, clicked: 0, bounced: 0, complained: 0, unsubscribed: 0 },
    });
    return ref.id;
  }, []);

  const updateDraft = useCallback(async (sendId, patch) => {
    setError(null);
    try {
      await setDoc(doc(db, 'eshot_sends', sendId), {
        ...patch,
        updatedAt: serverTimestamp(),
        lastEditedAt: serverTimestamp(),
      }, { merge: true });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteSend = useCallback(async (sendId) => {
    await deleteDoc(doc(db, 'eshot_sends', sendId));
  }, []);

  return { createDraft, copyToDraft, updateDraft, deleteSend, error };
}
